
import React, { useState } from 'react';
import './ProductCard.css';
import { Product, useApp } from '../../context/AppContext';

interface ProductCardProps {
  product: Product;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const { setSelectedProduct, toggleWishlist, currentUser, addToCart, setActiveTab } = useApp();
  const isWishlisted = currentUser.wishlist.includes(product.id);
  const offerCount = product.offers?.length || 0;
  const topOffer = product.topOffer;
  const isHot = offerCount >= 3;
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  // Pick a sane default size for retail flow: first in-stock size, else
  // 'OS' (one size). Cart distinguishes line items by (productId, size),
  // so an empty string would silently merge sizeless items, which we
  // don't want.
  const defaultSize = (() => {
    const sized = product.sizes?.find(s => s.stock > 0);
    if (sized) return sized.size;
    if (product.sizes?.[0]) return product.sizes[0].size;
    return 'OS';
  })();

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (adding || added) return;
    setAdding(true);
    addToCart(product.id, defaultSize, 1);
    setAdding(false);
    setAdded(true);
    // Reset the visual "added" state shortly so the same card stays usable.
    setTimeout(() => setAdded(false), 1500);
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart(product.id, defaultSize, 1);
    setActiveTab('cart');
  };

  return (
    <div className="product-card animate-scale-in" onClick={() => setSelectedProduct(product)}>
      <div className="product-image">
        <img src={product.images[0]} alt={product.name} />
        <div className="card-badges">
          <span className="unique-badge">1 of 1</span>
          {isHot && <span className="hot-badge">Hot</span>}
        </div>
        {topOffer && (
          <div className="card-top-offer">
            <span>${topOffer}</span>
          </div>
        )}
        <button
          className={`wishlist-btn ${isWishlisted ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
        >
          <svg viewBox="0 0 24 24" fill={isWishlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
          </svg>
        </button>
      </div>
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <div className="product-meta">
          {offerCount > 0 ? (
            <span className="offer-count">{offerCount} offer{offerCount !== 1 ? 's' : ''}</span>
          ) : (
            <span className="no-offers">Be first to offer</span>
          )}
        </div>
        <span className="product-unlock-badge" aria-label="Includes exclusive photos with purchase">
          ✨ + Exclusive photos with purchase
        </span>
        <div className="product-price">
          <span className="starting-bid-label">Starting bid</span>
          <span className="starting-bid-amount">${(product.salePrice || product.price).toFixed(0)}</span>
        </div>
        <div className="product-cta-row">
          <button
            type="button"
            className={`add-to-cart-btn ${added ? 'added' : ''}`}
            onClick={handleAddToCart}
            disabled={adding}
            aria-label={`Add ${product.name} to cart`}
          >
            {added ? '✓ Added' : adding ? 'Adding…' : 'Add to cart'}
          </button>
          <button
            type="button"
            className="buy-now-btn"
            onClick={handleBuyNow}
            aria-label={`Buy ${product.name} now`}
          >
            Buy now
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
